import {
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { AccountingApiService } from '../../../../_services/accounting-service/accounting-api/accounting-api.service';
import {
  AccountingContact,
  AccountingSyncStatus,
  AccountingSyncType,
} from '../../../../_interfaces/accounting.interface';
import { NgFooterTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { BoFormsModule } from '@bo/ng-forms';
import { concat, distinctUntilChanged, merge, Observable, of, Subject, Subscription, throwError, timer } from 'rxjs';
import {
  ClearContactsSyncStatus,
  SetContactsSyncStatus,
  StartPollingContactSyncStatus,
  StopPollingAccountingSyncStatus,
} from '../../../../store/transactions/transactions.actions';
import { Store } from '@ngxs/store';
import { ToastService } from '../../../../_services/toast-service/toast.service';
import { TransactionsSelectors } from '../../../../store/transactions/transactions.selectors';
import { SelectOption } from '../../../../_interfaces/select-field.interface';
import { ResponsiveService } from '../../../../_services/responsive-service/responsive.service';
import { TransactionsState } from '../../../../store/transactions/transactions.state';
import { catchError, debounceTime, retry, switchMap, tap } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-accounting-contacts-select',
  imports: [NgSelectComponent, BoFormsModule, AsyncPipe, NgFooterTemplateDirective],
  templateUrl: './accounting-contacts-select.component.html',
})
export class AccountingContactsSelectComponent implements OnInit, OnDestroy {
  readonly accountingApiService = inject(AccountingApiService);
  readonly store = inject(Store);
  readonly toastService = inject(ToastService);
  readonly responsiveService = inject(ResponsiveService);
  readonly transactionsState = inject(TransactionsState);
  readonly busySyncing$ = this.store.select(TransactionsSelectors.busySyncing);
  readonly syncStatus$ = this.store.select(TransactionsSelectors.contactsSyncStatus);

  disabled = input<boolean>(false);

  @Input() set selectedContact(value: AccountingContact | null) {
    // ng-select requires null (not undefined or empty object) to show placeholder
    if (value?.name) {
      this._selectedContact = value.name;
      this.preSelectedContact = value; // Store for an initial load
    } else {
      this._selectedContact = null;
      this.preSelectedContact = null;
    }
  }
  get selectedContact(): AccountingContact | null {
    return this._selectedContact ? { id: null, name: this._selectedContact } : null;
  }
  protected _selectedContact: string | null = null;
  private preSelectedContact: AccountingContact | null = null;
  @Output() selectionChanged: EventEmitter<AccountingContact> = new EventEmitter<AccountingContact>();
  @Output() busySyncingButtonTrue: EventEmitter<boolean> = new EventEmitter<boolean>();

  protected readonly isMobile = this.responsiveService.isMobile;
  contacts$!: Observable<SelectOption[]>;
  searchInput$ = new Subject<string>();
  private readonly reloadContacts$ = new Subject<void>(); // Dedicated subject for reloading contacts
  private readonly contactsSubject$ = new Subject<SelectOption[]>(); // Subject to emit updated contacts
  errorMessage = input<string>(''); // Error message to display (for is-invalid class)
  busySyncing = signal(false);
  syncStatus: WritableSignal<AccountingSyncStatus | undefined> = signal(undefined);
  loadingContacts = signal(false);
  totalContacts = signal(0);
  hasLargeContactList = computed(() => this.totalContacts() > 20);

  // Pagination state
  private currentPage = 1;
  private readonly PAGE_SIZE = 50;
  private hasMorePages = true;
  private allLoadedContacts: SelectOption[] = [];
  private isSearchMode = false;
  private currentSearchTerm = '';

  showValidation = computed(() => !!this.errorMessage());
  readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.initializeContactsObservable();

    // Check the initial sync status on a component load
    this.store.dispatch(new SetContactsSyncStatus());

    this.subscriptions.add(
      this.busySyncing$.subscribe({
        next: (res) => {
          this.busySyncing.set(res);
          if (res) {
            this.busySyncingButtonTrue.emit(true);
          } else {
            this.busySyncingButtonTrue.emit(false);
          }
        },
      }),
    );

    this.subscriptions.add(
      this.syncStatus$.subscribe({
        next: (data) => {
          if (data) this.syncStatus.set(data);
          if (this.syncStatus() === AccountingSyncStatus.Busy && !this.busySyncing()) {
            this.store.dispatch(new StartPollingContactSyncStatus());
          }
          if (this.syncStatus() === AccountingSyncStatus.Done || this.syncStatus() === AccountingSyncStatus.Fail) {
            this.resetAndReloadContacts();
          }
        },
      }),
    );

    // Subscribe to stopPolling$ once during initialisation
    // Note: We only reset busySyncing here, the actual reload happens in syncStatus$ subscription
    this.subscriptions.add(
      this.transactionsState.stopPolling$.subscribe({
        next: () => {
          this.busySyncing.set(false);
        },
      }),
    );
  }

  /**
   * Initialise the contacts observable with typeahead search and initial load
   */
  private initializeContactsObservable(): void {
    this.contacts$ = merge(
      // Initial load - first page of contacts
      concat(
        this.loadInitialContactsObservable(),
        this.searchInput$.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          tap(() => this.loadingContacts.set(true)),
          switchMap((term) => this.searchContacts(term)),
        ),
      ),
      // Manual reload trigger (e.g., after sync completes)
      this.reloadContacts$.pipe(
        tap(() => {
          this.loadingContacts.set(true);
        }),
        switchMap(() => this.loadInitialContactsObservable()),
      ),
      // Updates from infinite scroll
      this.contactsSubject$.asObservable(),
    );
  }

  /**
   * Load the initial page of contacts and return as observable
   */
  private loadInitialContactsObservable(): Observable<SelectOption[]> {
    this.loadingContacts.set(true);
    this.isSearchMode = false;
    this.currentPage = 1;
    this.allLoadedContacts = [];

    return this.accountingApiService.getAccountingContacts(this.currentPage, this.PAGE_SIZE).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse) => {
          // Only retry on auth errors
          if (error.status === 401 || error.status === 403) {
            // Wait 1 second before retrying to allow token refresh
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      tap((data) => {
        const contactOptions = this.mapContactsToSelectOptions(data.results);

        // Ensure a pre-selected contact is in the list (if not already present)
        if (this.preSelectedContact?.name) {
          const isAlreadyInList = contactOptions.some((contact) => contact.value === this.preSelectedContact!.name);

          if (!isAlreadyInList) {
            // Add pre-selected contact to the beginning of the list
            contactOptions.unshift({
              option: this.preSelectedContact.name,
              value: this.preSelectedContact.name,
            });
          }
        }

        this.allLoadedContacts = contactOptions;
        this.totalContacts.set(data.count);
        this.hasMorePages = !!data.next;
        this.loadingContacts.set(false);
      }),
      switchMap(() => of(this.allLoadedContacts)),
      catchError(() => {
        this.loadingContacts.set(false);
        return of([]);
      }),
    );
  }

  /**
   * Search contacts by name (server-side search)
   */
  private searchContacts(searchTerm: string): Observable<SelectOption[]> {
    // Store current search term for pagination
    this.currentSearchTerm = searchTerm;

    // If the search term is empty, reload initial contacts
    if (!searchTerm || searchTerm.trim() === '') {
      this.isSearchMode = false;
      this.currentSearchTerm = '';
      return this.loadInitialContactsObservable();
    }

    this.isSearchMode = true;
    this.currentPage = 1;

    return this.accountingApiService.getAccountingContacts(this.currentPage, this.PAGE_SIZE, searchTerm).pipe(
      retry({
        count: 2,
        delay: (error: HttpErrorResponse, retryCount) => {
          if (error.status === 401 || error.status === 403) {
            console.log(`[AccountingContactsSelect] Search - Auth error detected (attempt ${retryCount}), retrying...`);
            return timer(1000);
          }
          return throwError(() => error);
        },
      }),
      tap((data) => {
        this.allLoadedContacts = this.mapContactsToSelectOptions(data.results);
        this.totalContacts.set(data.count);
        this.hasMorePages = !!data.next;
        this.loadingContacts.set(false);
      }),
      switchMap(() => of(this.allLoadedContacts)),
      catchError(() => {
        this.loadingContacts.set(false);
        return of([]);
      }),
    );
  }

  /**
   * Load more contacts when scrolling to the end (infinite scroll)
   */
  onScrollToEnd(): void {
    if (!this.hasMorePages || this.loadingContacts()) {
      return;
    }

    this.loadingContacts.set(true);
    this.currentPage++;

    // Use the current search term if in search mode
    const searchTerm = this.isSearchMode ? this.currentSearchTerm : undefined;

    this.accountingApiService
      .getAccountingContacts(this.currentPage, this.PAGE_SIZE, searchTerm)
      .pipe(
        retry({
          count: 2,
          delay: (error: HttpErrorResponse, retryCount) => {
            if (error.status === 401 || error.status === 403) {
              console.log(
                `[AccountingContactsSelect] Scroll - Auth error detected (attempt ${retryCount}), retrying...`,
              );
              return timer(1000);
            }
            return throwError(() => error);
          },
        }),
        catchError((error: HttpErrorResponse) => {
          this.currentPage--; // Revert page increment on error
          this.loadingContacts.set(false);

          if (error.status === 401 || error.status === 403) {
            this.toastService.show('Session expired. Please refresh the page.', 'iconoir-warning-triangle');
          } else {
            this.toastService.show('Failed to load more contacts', 'iconoir-warning-triangle');
          }

          return throwError(() => error);
        }),
      )
      .subscribe({
        next: (data) => {
          const newContacts = this.mapContactsToSelectOptions(data.results);
          this.allLoadedContacts = [...this.allLoadedContacts, ...newContacts];
          this.hasMorePages = !!data.next;
          this.loadingContacts.set(false);

          // Emit an updated contacts array to trigger a UI update
          this.contactsSubject$.next([...this.allLoadedContacts]);
        },
      });
  }

  /**
   * Handle scroll events for preloading (optional - for smoother UX)
   */
  onScroll({ end }: { start: number; end: number }): void {
    if (this.loadingContacts() || !this.hasMorePages) {
      return;
    }

    // Preload the next page when the user is 80% through the current list
    const threshold = this.allLoadedContacts.length * 0.8;
    if (end > threshold) {
      this.onScrollToEnd();
    }
  }

  /**
   * Reset pagination state and reload contacts from the beginning
   */
  private resetAndReloadContacts(): void {
    this.currentPage = 1;
    this.allLoadedContacts = [];
    this.hasMorePages = true;
    this.isSearchMode = false;
    this.currentSearchTerm = '';
    this.reloadContacts$.next();
  }

  /**
   * Map API contacts to SelectOption format
   */
  private mapContactsToSelectOptions(contacts: AccountingContact[]): SelectOption[] {
    return contacts
      .filter((contact) => contact.name && contact.name.trim() !== '')
      .map<SelectOption>((contact) => ({
        option: contact.name!,
        value: contact.name!,
      }));
  }

  changeSelection(): void {
    const contact: AccountingContact = this._selectedContact
      ? { id: null, name: this._selectedContact }
      : { id: null, name: '' };
    this.selectionChanged.emit(contact);
  }

  addNewContact = (term: string): string => {
    return term;
  };

  /**
   * Check if a contact name already exists in the loaded contacts list
   */
  isContactInList(searchTerm: string): boolean {
    if (!searchTerm) return false;
    const normalizedSearch = searchTerm.toLowerCase().trim();
    return this.allLoadedContacts.some((contact) => contact.value.toLowerCase().trim() === normalizedSearch);
  }

  /**
   * Add and immediately select a new contact from the header
   */
  addAndSelectNewContact(searchTerm: string, ngSelectInstance: NgSelectComponent): void {
    if (!searchTerm || this.isContactInList(searchTerm)) return;

    // Set as selected
    this._selectedContact = this.addNewContact(searchTerm);
    this.changeSelection();

    // Close the dropdown
    if (ngSelectInstance) {
      ngSelectInstance.close();
    }
  }

  syncContacts() {
    this.busySyncing.set(true);
    // Detect immediate user click as the state takes a second to update
    this.busySyncingButtonTrue.emit(true);

    // Clear and start polling BEFORE making the API call
    // This ensures polling begins immediately, not after the PUT request completes
    this.store.dispatch(new ClearContactsSyncStatus());
    this.store.dispatch(new StartPollingContactSyncStatus());

    this.subscriptions.add(
      this.accountingApiService.requestAccountingSync(AccountingSyncType.Contacts).subscribe({
        next: () => {
          // Sync request initiated successfully - polling will track the status
        },
        error: (error) => {
          this.busySyncing.set(false);
          this.busySyncingButtonTrue.emit(false);
          this.toastService.show(
            `Something went wrong. ${error.error?.non_field_errors ? error.error?.non_field_errors : ''}`,
            'iconoir-warning-triangle',
          );
          this.store.dispatch(new StopPollingAccountingSyncStatus());
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected readonly Boolean = Boolean;
}
